import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, Check, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscribe = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      toast.success('Thanks for subscribing!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You\'re already subscribed!');
        setSubscribed(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="subscribe-page">
      <div className="w-full max-w-md">
        {subscribed ? (
          <div className="text-center">
            <div className="bg-green-100 p-4 rounded-full w-fit mx-auto mb-6">
              <Check className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold mb-3">You're Subscribed!</h1>
            <p className="text-muted-foreground mb-8">
              We'll keep you updated on new arrivals and special offers.
            </p>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                onClick={() => setSubscribed(false)}
                className="w-full"
              >
                Subscribe another email
              </Button>
              <Link to="/" className="block">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Shop
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-6">
              <Mail className="h-12 w-12 text-primary" />
            </div>
            <h1 className="font-serif text-3xl font-bold mb-3">Stay Updated</h1>
            <p className="text-muted-foreground mb-8">
              Enter your email to receive updates on new arrivals and exclusive offers.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="pl-10 h-12"
                  autoFocus
                  data-testid="subscribe-email"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full btn-accent h-12"
                disabled={loading || !email}
                data-testid="subscribe-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Subscribing...
                  </span>
                ) : (
                  'Subscribe'
                )}
              </Button>
            </form>
            
            <p className="text-xs text-muted-foreground mt-4">
              No spam. Unsubscribe anytime.
            </p>
            
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mt-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Shop
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscribe;
